import type { DocMigration } from "../docMigration";

const docEntityTableFieldsToEntityType = {
  underlays: "UNDERLAY",
  assets: "ASSET",
  backupPropSettingsSets: "SETTINGS",
  blocks: "BLOCK",
  rooms: "ROOM",
  roomLayouts: "ROOM_LAYOUT",
  roomLayoutInstances: "ROOM_LAYOUT_INSTANCE",
  stairSystems: "STAIR_SYSTEM",
  stairBlocks: "STAIR_BLOCK",
  railings: "RAILING",
  railingPosts: "RAILING_POST",
  roofSystems: "ROOF_SYSTEM",
  selectors: "SELECTOR",
  viewOverrides: "VIEW_OVERRIDE",
  viewSelectors: "VIEW_SELECTOR",
  roofs: "ROOF",
  roofVolumes: "ROOF_VOLUME",
  foundationSystems: "FOUNDATION_SYSTEM",
  foundations: "FOUNDATION",
  elevations: "ELEVATION",
  facades: "FACADE",
  facadeSpanStrata: "FACADE_SPAN_STRATUM",
  facadeSweepStrata: "FACADE_SWEEP_STRATUM",
  referenceGeom: "REFERENCE_GEOM",
  sheets: "SHEET",
  drawingRefs: "DRAWING_REF",
  palettes: "PALETTE",
  annotation: "ANNOTATION",
  edgeBehaviors: "EDGE_BEHAVIOR",
  casework: "CASEWORK",
  ceilings: "CEILING",
  propInstances: "PROP_INSTANCE",
  propDefs: "PROP_DEF",
  propAttributes: "PROP",
  levels: "LEVEL",
  roofOpenings: "ROOF_OPENING",
  groups: "GROUP",
  settingsSets: "SETTINGS_SET",
  entitySettings: "ENTITY_SETTINGS",
  settings: "SETTINGS",
  optionTemplates: "OPTION_TEMPLATE",
  dormers: "DORMER",
  skylights: "SKYLIGHT",
  modifiers: "MODIFIER",
  edgeModifiers: "EDGE_MODIFIER",
  measurements: "MEASUREMENT",
  openings: "OPENING",
  floors: "FLOOR",
  floorFinishes: "FLOOR_FINISH",
  sites: "SITE",
  siteShapes: "SITE_SHAPE",
  optionEffects: "OPTION_EFFECT",
  optionRules: "OPTION_RULE",
  optionSetOverrides: "OPTION_SET_OVERRIDES",
  optionSets: "OPTION_SET",
  optionLocks: "OPTION_LOCK",
  auxOptionSets: "AUX_OPTION_SET",
  columns: "COLUMN",
  view3Ds: "VIEW_3D",
  planViews: "PLAN_VIEW",
  elevationViews: "ELEVATION_VIEW",
  optionSetRefs: "OPTION_SET_REF",
  builderRefs: "BUILDER_REF",
  electricalDevices: "ELECTRICAL_DEVICE",
  electricalLayouts: "ELECTRICAL_LAYOUT",
  planSetTemplates: "PLAN_SET_TEMPLATE",
  planSetTemplateConfigs: "PLAN_SET_TEMPLATE_CONFIG",
  contentTemplates: "CONTENT_TEMPLATE",
  contentTemplateInstances: "CONTENT_TEMPLATE_INSTANCE",
  contentReplicators: "CONTENT_REPLICATOR",
  localParameters: "LOCAL_PARAMETER",
  globalParameters: "GLOBAL_PARAMETER",
  viewports: "VIEWPORT",
  viewTemplates: "VIEW_TEMPLATE",
  viewTemplateOverrides: "VIEW_TEMPLATE_OVERRIDE",
  dataTables: "DATA_TABLE",
  dataSequences: "DATA_SEQUENCE",
  sectionViews: "SECTION_VIEW",
  presets: "PRESET",
  entityTags: "ENTITY_TAG",
  effects: "EFFECT",
  datums: "DATUM",
  ruleBasedSelectors: "RULE_BASED_SELECTOR",
  ruleBasedSelectorInstances: "RULE_BASED_SELECTOR_INSTANCE",

  // Not entity types
  locked: undefined,
  hidden: undefined,
  entityRoomLayouts: undefined,
  entityGroups: undefined,
  entityRefs: undefined,
  optionChoices: undefined,
  docRefs: undefined,
  facadeSpanStratumAssociations: undefined,
  facadeSweepStratumAssociations: undefined,
  datumSpanAssociations: undefined,
} as const;

const docRefDirectEligibleEntities = {
  GROUP: true,
  SITE: true,
  SHEET: true,
  DATUM: true,
  FACADE: true,
  PRESET: true,
  PALETTE: true,
  MODIFIER: true,
  ELEVATION: true,
  OPTION_SET: true,
  ENTITY_TAG: true,
  ROOM_LAYOUT: true,
  OPTION_RULE: true,
  OPTION_LOCK: true,
  SETTINGS_SET: true,
  VIEW_TEMPLATE: true,
  DATA_SEQUENCE: true,
  CONTENT_TEMPLATE: true,
  GLOBAL_PARAMETER: true,
  PLAN_SET_TEMPLATE: true,
  PROP: true,
  RULE_BASED_SELECTOR: true,
  VIEW_TEMPLATE_OVERRIDE: true,
} as const;

const docRefIndirectEligibleEntities = {
  ROOM: true,
  ASSET: true,
  EFFECT: true,
  OPENING: true,
  SETTINGS: true,
  CASEWORK: true,
  VIEWPORT: true,
  ANNOTATION: true,
  DATA_TABLE: true,
  SITE_SHAPE: true,
  DRAWING_REF: true,
  OPTION_EFFECT: true,
  PROP_INSTANCE: true,
  AUX_OPTION_SET: true,
  LOCAL_PARAMETER: true,
  CONTENT_REPLICATOR: true,
  FACADE_SPAN_STRATUM: true,
  FACADE_SWEEP_STRATUM: true,
  PLAN_SET_TEMPLATE_CONFIG: true,
  CONTENT_TEMPLATE_INSTANCE: true,
  RULE_BASED_SELECTOR_INSTANCE: true,
} as const;

function isDocRefEligibleEntityType(type: string): boolean {
  return [...Object.keys(docRefDirectEligibleEntities), ...Object.keys(docRefIndirectEligibleEntities)].includes(type);
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_467: DocMigration = {
  maxSchemaVersion: "0.0.467",
  description: "Add missing entityType to entityRefs that were imported via addMissingDocRefEntities",
  apply(json: any): any {
    const invalidEntityRefs: { [id: string]: { [key: string]: any } } = {};
    const invalidIds = new Set<string>();

    for (const id in json.entityRefs) {
      const entityRef = json.entityRefs[id];

      if (entityRef.entityType) {
        continue;
      }

      invalidEntityRefs[id] = entityRef;
      invalidIds.add(id);
    }

    if (!Object.keys(invalidEntityRefs).length) {
      return json;
    }

    for (const field in docEntityTableFieldsToEntityType) {
      if (!invalidIds.size) {
        break;
      }

      const entityType = docEntityTableFieldsToEntityType[field];

      if (!entityType || !isDocRefEligibleEntityType(entityType)) {
        continue;
      }

      const docTable = json[field];

      if (!docTable) {
        continue;
      }

      for (const id in docTable) {
        if (!invalidEntityRefs[id]) {
          continue;
        }

        invalidEntityRefs[id].entityType = entityType;
        invalidIds.delete(id);
      }
    }

    return json;
  },
};

export default migration_0_0_467;
